import React, { useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";

import CardTitle from "./CardTitle";

import backFace from "../../assets/images/back.png";
import diamonds from "../../assets/images/diamonds.png";
import hearts from "../../assets/images/hearts.png";
import spades from "../../assets/images/spades.png";
import clubs from "../../assets/images/clubs.png";
import topikPositive from "../../assets/images/topik-positive.svg?url";
import topikNegative from "../../assets/images/topik-negative.svg?url";

import "./card.scss";

const w = () => (window.width <= 1920 ? 220 : 300);
const cardLayoutClass = () => (w() > 220 ? "big-cards" : "");

// color is the suit's surface colour (separator). textColor is its darker sibling for the title
// and for the suit band behind white text: the suit colours are 1.8-3.9:1 as text on white, these
// are at least 4.5:1 (WCAG 1.4.3). Same values as $*-text-color in the stylesheets, except clubs:
// this card's clubs face is the yellow #fbba00 ($clubs-color-light), so its text colour is that
// yellow darkened (#886500, 5.37:1), not $clubs-text-color (#a65600), which darkens the orange $clubs-color.
const suitConfig = {
  diamonds: {
    icon: diamonds,
    color: "#59aa3b",
    textColor: "#3f792a",
  },
  hearts: {
    icon: hearts,
    color: "#e14970",
    textColor: "#cd224e",
  },
  spades: {
    icon: spades,
    color: "#00587d",
    textColor: "#00587d",
  },
  clubs: {
    icon: clubs,
    color: "#fbba00",
    textColor: "#886500", // the same yellow hue, darker
  },
  // Topik decks: flat colorblind-safe colors (Okabe-Ito blue/vermillion) plus a +/- icon, so
  // polarity is never conveyed by color alone.
  positive: {
    icon: topikPositive,
    color: "#0072B2",
    textColor: "#0072B2",
  },
  negative: {
    icon: topikNegative,
    color: "#D55E00",
    textColor: "#D55E00",
  },
};

const minDescriptionFontSize = 10;

// Steps the description down from its stylesheet size until it fits the lower half of the card.
// Only needed when the longest description meets the suit band, or a narrow card.
function fitDescription(descriptionElement) {
  const content = descriptionElement?.parentElement;
  if (!content) {
    return;
  }

  // The content ends above the suit band, which is taller when a long suit name wraps.
  const band = content.parentElement.querySelector(".card-suit-band");
  content.style.bottom = band ? `${band.offsetHeight + 6}px` : "";
  if (content.clientHeight === 0) {
    return;
  }

  const { style } = descriptionElement;
  style.fontSize = "";
  const available = () => content.clientHeight - descriptionElement.offsetTop;
  let size = parseFloat(window.getComputedStyle(descriptionElement).fontSize);
  while (descriptionElement.offsetHeight > available() && size > minDescriptionFontSize) {
    size -= 0.5;
    style.fontSize = `${size}px`;
  }
}

const Card = ({
  cardId,
  cardKey,
  title,
  forwardRef,
  description,
  isDiscarded = false,
  suit,
  suitTitle = null,
  suitLabel = null,
  exposed = false,
  style = {},
  onClick = () => {},
}) => {
  const { icon, color, textColor } = suitConfig[suit];
  const descriptionRef = useRef(null);

  useLayoutEffect(() => {
    const fit = () => fitDescription(descriptionRef.current);
    fit();

    // Again once Work Sans has loaded and whenever the window (and so the card size) changes.
    let cancelled = false;
    document.fonts?.ready.then(() => { if (!cancelled) fit(); });
    window.addEventListener("resize", fit);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", fit);
    };
  }, [description, suitTitle]);

  const hasOverlyLongWords = description.split(" ").some((word) => word.length > 25);
  const descriptionStyle = hasOverlyLongWords ? { overflowWrap: "break-word" } : {};

  const suitIconStyle = {
    backgroundImage: `url(${icon})`,
  };

  // `exposed`: a flat, always-face-up variant that renders only the front face and drops the
  // 3D-flip machinery (preserve-3d / rotateY / backface-visibility). Print/PDF engines flatten
  // 3D transforms and ignore backface-visibility, so the double-sided card paints its dark back
  // over the front - see TopikCompleteView. The exposed variant is safe to print.
  return (
    <div
      ref={forwardRef}
      data-cardid={cardId || cardKey}
      className={`double-sided-card card-faces-container ${cardLayoutClass()} ${
        exposed ? "card-faces-container--exposed" : ""
      } ${suitTitle ? "card-faces-container--with-suit-title" : ""} ${isDiscarded ? "discarded" : ""}`}
      onClick={() => {
        onClick(cardId, cardKey);
      }}
      style={style}>
      <div
        className={`card ${suitTitle ? "card--with-suit-title" : ""}`}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
        }}>
        <div className="card-content">
          <CardTitle title={title} color={textColor} />
          <div className="card-separator" style={{ backgroundColor: color }} />
          <p className="card-description" style={descriptionStyle} ref={descriptionRef}>
            {description}
          </p>
        </div>
        {suitTitle && (
          <div className="card-suit-band" style={{ backgroundColor: textColor }}>
            <span>{suitTitle}</span>
          </div>
        )}
        {/* The suit icons are background images: name the suit for screen readers when the band is off. */}
        {!suitTitle && suitLabel && <span className="visually-hidden">{suitLabel}</span>}
        <div className="card-suit-img card-suit-img-1" style={suitIconStyle}></div>
        <div className="card-suit-img card-suit-img-2" style={suitIconStyle}></div>
      </div>
      {!exposed && (
        <div className="card-back" style={{ background: `url(${backFace}) #555` }}></div>
      )}
    </div>
  );
};

Card.propTypes = {
  cardId: PropTypes.string.isRequired,
  cardKey: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isDiscarded: PropTypes.bool,
  suit: PropTypes.string.isRequired,
  // Printed at the bottom of the card; pass it only when the deck has it turned on.
  suitTitle: PropTypes.string,
  // Suit name read by screen readers when the suit title band is not shown.
  suitLabel: PropTypes.string,
  exposed: PropTypes.bool,
  style: PropTypes.object,
  forwardRef: PropTypes.any,
  onClick: PropTypes.func,
};

export default Card;
